/**
 * Fast per-cell optimal-filing-age solver for a couple.
 *
 * `optimalStrategyCoupleFast` is a drop-in replacement for
 * `optimalStrategyCouple` / `optimalStrategyCoupleOptimized` that produces
 * identical results (exact NPV match) in a fraction of the time.
 *
 * ## Problem
 *
 * Given a couple with known (fixed) death dates, find the (earnerFiling,
 * depFiling) pair that maximizes NPV of lifetime benefits. This is called
 * once per grid cell in the StrategyMatrix UI (~14×14 cells).
 *
 * The slow reference iterates F×F filing pairs (F ≈ 96 months) and calls
 * `strategySumCentsCouple`, which re-walks the benefit periods and allocates
 * fresh `BenefitPeriod`/`MonthDate`/`Money` objects per pair.
 *
 * ## Optimizations
 *
 * 1. Raw numbers throughout. Filing dates are epoch-months (Int32), amounts
 *    are cents (Float64). No allocations in the hot loop.
 * 2. Pre-tabulated discount-factor table `dkF[k] = (1+r)^-k` covering the
 *    entire range from currentDate to max(earnerDeath, depDeath). Replaces
 *    `Math.pow` calls with O(1) array lookups.
 * 3. Per-filing-age benefit amounts (filing-year and post-January) are
 *    pre-tabulated per recipient (nEF + nDF values).
 * 4. Earner personal NPV is independent of dep filing; precomputed once per
 *    earner filing age.
 * 5. Dep personal NPV (full and truncated-at-earnerDeath) is independent of
 *    earner filing age in the common `!depZeroPia` case; precomputed once
 *    per dep filing age.
 *
 * The inner (fe, fd) loop does O(1) arithmetic + two small helper calls
 * (spousal/survivor amount) + a constant-amount NPV via discount-factor
 * differences.
 *
 * ## Correctness
 *
 * Arithmetic matches `strategySumPeriodsCouple` in strategy-calc.ts to the
 * last rounding step. Validated against 1000 golden cases generated by the
 * slow reference; see `src/test/strategy/grid-optimal-goldens.test.ts`.
 */

import {
  eligibleForSpousalBenefit,
  MAX_BENEFIT_AGE_MONTHS,
  MIN_SURVIVOR_BENEFIT_RATIO,
} from '$lib/benefit-calculator';
import { type MonthDate, MonthDuration } from '$lib/month-time';
import type { Recipient } from '$lib/recipient';
import { type AlreadyFiled, NOT_FILED } from './already-filed';
import { classifyEarnerDependent } from './earner-dependent.js';
import {
  calculateMonthlyDiscountRate,
  filingAgeRange,
} from './strategy-calc.js';

// ─────────────────────────────────────────────────────────────────────────
// Shared primitives
//
// The four functions below (benefitCentsAtAge, filingYearCents,
// spousalCentsForPair, survivorCentsCalc) are byte-for-byte copies of the
// same-named helpers in the "Shared primitives" block of expected-npv.ts.
// (Named rather than line-numbered: line ranges rot on the next edit.) They are
// duplicated rather than imported to keep this hot-path module
// self-contained for inlining. Authoritative docstrings (early-filing
// rules, "January bump", survivor ratio, etc.) live in expected-npv.ts;
// consult those when editing.
//
// Drift between the two copies will only surface through golden tests
// (grid-optimal-goldens.test.ts + expected-npv-couple-goldens.test.ts),
// so edit both copies together. Extracting to a shared module is a
// pending follow-up.
// ─────────────────────────────────────────────────────────────────────────

/** Personal benefit at a given filing age, in cents. */
function benefitCentsAtAge(
  piaDollarCents: number,
  nraMonths: number,
  delayedRetirementIncrease: number,
  ageMonths: number
): number {
  let mult: number;
  if (nraMonths > ageMonths) {
    const before = nraMonths - ageMonths;
    mult = -(
      (Math.min(36, before) * 5) / 900 +
      (Math.max(0, before - 36) * 5) / 1200
    );
  } else {
    // Delayed credits stop accruing at age 70.
    const creditedMonths =
      ageMonths < MAX_BENEFIT_AGE_MONTHS ? ageMonths : MAX_BENEFIT_AGE_MONTHS;
    const after = creditedMonths - nraMonths;
    mult = (delayedRetirementIncrease / 12) * after;
  }
  return Math.floor(Math.round(piaDollarCents * (1 + mult)) / 100) * 100;
}

/** Personal benefit during the filing year (before next January), in cents. */
function filingYearCents(
  piaDollarCents: number,
  nraMonths: number,
  delayedRetirementIncrease: number,
  ageMonths: number,
  ssaBirthEpoch: number,
  nraEpoch: number
): number {
  const filingEpoch = ssaBirthEpoch + ageMonths;
  if (
    filingEpoch <= nraEpoch ||
    ageMonths >= MAX_BENEFIT_AGE_MONTHS ||
    filingEpoch % 12 === 0
  )
    return benefitCentsAtAge(
      piaDollarCents,
      nraMonths,
      delayedRetirementIncrease,
      ageMonths
    );
  const thisJan = filingEpoch - (filingEpoch % 12);
  const compAge = Math.max(nraEpoch, thisJan) - ssaBirthEpoch;
  return benefitCentsAtAge(
    piaDollarCents,
    nraMonths,
    delayedRetirementIncrease,
    compAge
  );
}

/** Spousal benefit in cents for a given filing pair. */
function spousalCentsForPair(
  depPiaRaw: number,
  earnerPiaRaw: number,
  depFilingEpoch: number,
  depNraEpoch: number,
  spousalStartEpoch: number,
  depFYCents: number,
  depPJCents: number
): number {
  const base = earnerPiaRaw / 2 - depPiaRaw;
  if (base <= 0) return 0;

  if (spousalStartEpoch >= depNraEpoch) {
    if (depFilingEpoch <= depNraEpoch) return Math.floor(base / 100) * 100;
    const startYear = Math.floor(spousalStartEpoch / 12);
    const filingYear = Math.floor(depFilingEpoch / 12);
    const personal = startYear > filingYear ? depPJCents : depFYCents;
    const excess = earnerPiaRaw / 2 - personal;
    return excess <= 0 ? 0 : Math.floor(excess / 100) * 100;
  }

  const mBefore = depNraEpoch - spousalStartEpoch;
  if (mBefore <= 36)
    return Math.floor((base * (1 - mBefore / 144)) / 100) * 100;

  const r1 = base * 0.25;
  const r2 = base * ((mBefore - 36) / 240);
  return Math.floor((base - r1 - r2) / 100) * 100;
}

/** Survivor benefit in cents. */
function survivorCentsCalc(
  earnerPiaRaw: number,
  earnerPiaDollar: number,
  earnerNra: number,
  earnerDelayedRetirementIncrease: number,
  earnerSsaBirth: number,
  earnerNraEpoch: number,
  earnerFilingEpoch: number,
  earnerDeathEpoch: number,
  earner70Epoch: number,
  depSurvNra: number,
  survStartEpoch: number,
  depSsaBirth: number
): number {
  let base: number;
  if (earnerFilingEpoch >= earnerDeathEpoch) {
    if (earnerDeathEpoch < earnerNraEpoch) {
      base = earnerPiaRaw;
    } else {
      const effEpoch = Math.min(earnerDeathEpoch, earner70Epoch);
      base = benefitCentsAtAge(
        earnerPiaDollar,
        earnerNra,
        earnerDelayedRetirementIncrease,
        effEpoch - earnerSsaBirth
      );
    }
  } else {
    const pct825 = Math.round(earnerPiaRaw * 0.825);
    const eBenefit = benefitCentsAtAge(
      earnerPiaDollar,
      earnerNra,
      earnerDelayedRetirementIncrease,
      earnerFilingEpoch - earnerSsaBirth
    );
    base = Math.floor(Math.max(pct825, eBenefit));
  }

  const survAge = survStartEpoch - depSsaBirth;
  if (survAge >= depSurvNra) return Math.floor(base / 100) * 100;

  const m60toNRA = depSurvNra - 720;
  const m60toAge = survAge - 720;
  // Computed exactly as survivorBenefit does: (1 - ratio), not a 0.285
  // literal, which is a different double and rounds half-cents differently.
  const ratio =
    MIN_SURVIVOR_BENEFIT_RATIO +
    (1 - MIN_SURVIVOR_BENEFIT_RATIO) * Math.max(0, m60toAge / m60toNRA);
  return Math.floor(Math.round(base * ratio) / 100) * 100;
}

// ─────────────────────────────────────────────────────────────────────────
// Main entry point
// ─────────────────────────────────────────────────────────────────────────

/**
 * Find the (filing0, filing1) pair that maximizes NPV of benefits for a
 * couple with known death dates. Matches `optimalStrategyCouple` to within a
 * cent (the golden tests' tolerance).
 *
 * Every return value is a real strategy. A recipient who dies before they
 * could file is searched at their single earliest age, where their personal
 * NPV is zero, rather than emptying the range — see the clamp below.
 *
 * A recipient recorded in `alreadyFiled` is searched at their actual filing
 * age only.
 */
export function optimalStrategyCoupleFast(
  recipients: [Recipient, Recipient],
  finalDates: [MonthDate, MonthDate],
  currentDate: MonthDate,
  discountRate: number,
  alreadyFiled: AlreadyFiled = NOT_FILED
): [MonthDuration, MonthDuration, number] {
  // ── Classify earner/dependent ──
  const { earner, dependent, earnerIndex, dependentIndex } =
    classifyEarnerDependent(recipients);

  // ── Recipient constants ──
  const ePiaRaw = earner.pia().primaryInsuranceAmount().cents();
  const ePiaDol = Math.floor(ePiaRaw / 100) * 100;
  const eNra = earner.normalRetirementAge().asMonths();
  const eDri = earner.delayedRetirementIncrease();
  const eSsaBirth = earner.birthdate
    .dateAtSsaAge(new MonthDuration(0))
    .monthsSinceEpoch();
  const eNraEpoch = eSsaBirth + eNra;
  const e70Epoch = eSsaBirth + MAX_BENEFIT_AGE_MONTHS;

  const dPiaRaw = dependent.pia().primaryInsuranceAmount().cents();
  const dPiaDol = Math.floor(dPiaRaw / 100) * 100;
  const dNra = dependent.normalRetirementAge().asMonths();
  const dDri = dependent.delayedRetirementIncrease();
  const dSsaBirth = dependent.birthdate
    .dateAtSsaAge(new MonthDuration(0))
    .monthsSinceEpoch();
  const dNraEpoch = dSsaBirth + dNra;
  const dSurvNra = dependent.survivorNormalRetirementAge().asMonths();

  if (dSurvNra <= 720) {
    throw new Error(
      `survivorNormalRetirementAge must exceed 720 months; got ${dSurvNra}`
    );
  }

  const depZeroPia = dPiaRaw === 0;
  const spEligible = eligibleForSpousalBenefit(dependent, earner);

  const mRate = calculateMonthlyDiscountRate(discountRate);
  if (!Number.isFinite(mRate)) {
    throw new Error(`monthlyDiscountRate must be finite; got ${mRate}`);
  }
  if (mRate !== 0 && mRate < 1e-9) {
    throw new Error(
      `monthlyDiscountRate must be either exactly 0 or >= 1e-9; got ${mRate}`
    );
  }

  const curEpoch = currentDate.monthsSinceEpoch();
  const curP1 = curEpoch + 1;

  // ── Death epochs (fixed per cell) ──
  const eDeath = finalDates[earnerIndex].monthsSinceEpoch();
  const dDeath = finalDates[dependentIndex].monthsSinceEpoch();

  // ── Filing ranges ──
  // A recipient with no filing choice left has one option, so their range
  // collapses to a single entry rather than going empty.
  const eRange = filingAgeRange(earner, currentDate, alreadyFiled[earnerIndex]);
  const dRange = filingAgeRange(
    dependent,
    currentDate,
    alreadyFiled[dependentIndex]
  );
  const eStart = eRange.earliest.asMonths();
  const dStart = dRange.earliest.asMonths();
  // Clamped to the death date, but never below the start: one recipient dying
  // before they could file does NOT make the couple's scenario meaningless —
  // the other still has a real filing decision, and the survivor benefit
  // still depends on it. Collapsing the dead recipient's range to a single
  // (immaterial) age preserves that, where inverting it would return the
  // [0, 0, -1] sentinel and throw the surviving spouse's answer away. The
  // slow reference reaches the same result by not clamping at all: every
  // filing age past death yields an identical NPV, so it settles on the
  // earliest, which is the one value kept here.
  const eEnd = Math.max(
    eStart,
    Math.min(
      eRange.latest.asMonths(),
      earner.birthdate.ageAtSsaDate(finalDates[earnerIndex]).asMonths()
    )
  );
  const dEnd = Math.max(
    dStart,
    Math.min(
      dRange.latest.asMonths(),
      dependent.birthdate.ageAtSsaDate(finalDates[dependentIndex]).asMonths()
    )
  );

  const nEF = eEnd - eStart + 1;
  const nDF = dEnd - dStart + 1;

  // ── Pre-tabulate discount factors dkF[k] = (1+r)^-k for k=0..maxLag+1 ──
  const maxEpoch = Math.max(eDeath, dDeath) + 2;
  const tableSize = maxEpoch - curEpoch + 1;
  // Invariant: tableSize >= 1, since each death epoch is at or after the
  // bucket start, which is never before currentDate. Assert
  // explicitly so a cryptic Float64Array RangeError can't bury this if
  // earliestFiling's semantics change.
  if (tableSize < 1) {
    throw new Error(
      `discount-factor table size must be >= 1; got ${tableSize} ` +
        `(curEpoch=${curEpoch}, eDeath=${eDeath}, dDeath=${dDeath})`
    );
  }
  const dkF = new Float64Array(tableSize);
  if (mRate === 0) {
    for (let k = 0; k < tableSize; k++) dkF[k] = 1;
  } else {
    dkF[0] = 1;
    const inv = 1 / (1 + mRate);
    for (let k = 1; k < tableSize; k++) dkF[k] = dkF[k - 1] * inv;
  }

  // Closed-form NPV of a constant amount `amt` paid monthly from
  // paymentStartEpoch through paymentEndEpoch (inclusive). "Payments received
  // at end of month for prior month's benefit" ⇒ first payment is at
  // paymentStartEpoch; discounted k months from currentDate.
  // Also respects currentDatePlusOne: payments before currentDate+1 are
  // skipped (caller is responsible for passing startEpoch >= curP1, but we
  // defensively clamp).
  const npvConstant = (amt: number, start: number, end: number): number => {
    if (amt === 0 || start > end) return 0;
    const es = start < curP1 ? curP1 : start;
    if (es > end) return 0;
    if (mRate === 0) return amt * (end - es + 1);
    const k1 = es - curEpoch;
    const k2 = end - curEpoch + 1;
    return (amt * (dkF[k1] - dkF[k2])) / mRate;
  };

  // Personal NPV for a recipient filing at `fileEpoch` and dying at
  // `deathEpoch`, with year-1 amount `fyCents` and post-January amount
  // `pjCents`. Mirrors strategySumPeriodsCouple's personal-benefit split.
  const personalNPV = (
    fileEpoch: number,
    deathEpoch: number,
    fyCents: number,
    pjCents: number
  ): number => {
    if (deathEpoch < fileEpoch) return 0;
    // Payments arrive end-of-month for the prior month, so the first payment
    // is at fileEpoch+1 (one month after filing) and the last is at
    // deathEpoch+1 (the month after death).
    const janEpoch = fileEpoch + (12 - (fileEpoch % 12));
    const part1End = janEpoch - 1 < deathEpoch ? janEpoch - 1 : deathEpoch;
    let npv = npvConstant(fyCents, fileEpoch + 1, part1End + 1);
    if (deathEpoch >= janEpoch) {
      npv += npvConstant(pjCents, janEpoch + 1, deathEpoch + 1);
    }
    return npv;
  };

  // ── Pre-compute benefit amounts per filing age ──
  const eFY = new Float64Array(nEF);
  const ePJ = new Float64Array(nEF);
  const ePersNPV = new Float64Array(nEF);
  for (let i = 0; i < nEF; i++) {
    const age = eStart + i;
    ePJ[i] = benefitCentsAtAge(ePiaDol, eNra, eDri, age);
    eFY[i] = filingYearCents(ePiaDol, eNra, eDri, age, eSsaBirth, eNraEpoch);
    ePersNPV[i] = personalNPV(eSsaBirth + age, eDeath, eFY[i], ePJ[i]);
  }

  const dFY = new Float64Array(nDF);
  const dPJ = new Float64Array(nDF);
  // `dPersFullNPV[i]` = dep personal NPV with no survivor truncation
  // (dFile .. dDeath). Used whenever the dep personal period is not cut
  // short by the survivor benefit kicking in.
  const dPersFullNPV = new Float64Array(nDF);
  // `dPersTruncNPV[i]` = dep personal NPV truncated at earner-death
  // (dFile .. min(dDeath, eDeath)). Used when survivor is active and the
  // dep outlives the earner.
  const dPersTruncNPV = new Float64Array(nDF);
  // For !depZeroPia these tables are independent of the earner's filing age
  // and can be precomputed per cell. (For depZeroPia we recompute inline.)
  if (!depZeroPia) {
    for (let i = 0; i < nDF; i++) {
      const age = dStart + i;
      dPJ[i] = benefitCentsAtAge(dPiaDol, dNra, dDri, age);
      dFY[i] = filingYearCents(dPiaDol, dNra, dDri, age, dSsaBirth, dNraEpoch);
      const dFile = dSsaBirth + age;
      dPersFullNPV[i] = personalNPV(dFile, dDeath, dFY[i], dPJ[i]);
      // Truncated endpoint is min(dDeath, eDeath). If dFile > that endpoint
      // the truncated period is empty.
      const truncEnd = dDeath < eDeath ? dDeath : eDeath;
      dPersTruncNPV[i] = personalNPV(dFile, truncEnd, dFY[i], dPJ[i]);
    }
  }

  // ── Main loop — iterate all filing pairs ──
  //
  // Iteration order chosen to match the slow reference's tie-breaking:
  // outer = f0 (recipients[0] filing), inner = f1. On identical NPV the
  // first-found pair wins, and `optimalStrategyCouple` uses the same order.
  let bestF0 = 0;
  let bestF1 = 0;
  let bestNPV = -1;

  const sf0 = earnerIndex === 0 ? eStart : dStart;
  const ef0 = earnerIndex === 0 ? eEnd : dEnd;
  const sf1 = earnerIndex === 0 ? dStart : eStart;
  const ef1 = earnerIndex === 0 ? dEnd : eEnd;

  for (let f0 = sf0; f0 <= ef0; f0++) {
    for (let f1 = sf1; f1 <= ef1; f1++) {
      const fe = earnerIndex === 0 ? f0 : f1;
      const fd = earnerIndex === 0 ? f1 : f0;
      const feI = fe - eStart;
      const fdI = fd - dStart;

      const eFile = eSsaBirth + fe;
      let dFile = dSsaBirth + fd;
      // Zero-PIA dep can't file before earner (they'd get $0 until spousal).
      if (depZeroPia && dFile < eFile) dFile = eFile;

      // Dep personal benefit amounts for this fd. For depZeroPia both are 0
      // (dep has no personal benefit of their own).
      const dfy = depZeroPia ? 0 : dFY[fdI];
      const dpj = depZeroPia ? 0 : dPJ[fdI];

      // Survivor start = max(earnerDeath+1, depFiling).
      const svStart = eDeath + 1 > dFile ? eDeath + 1 : dFile;

      // Survivor amount, if dep actually outlives the survivor-start date.
      // Reference (strategySumPeriodsCouple) compares survivor vs dep's
      // post-January personal benefit (benefitOnDate at filingDate+1y,
      // which equals benefitCentsAtAge). That value is already tabulated
      // as dPJ[fdI]; for zero-PIA dep it's 0.
      let survAmt = 0;
      let isSurvivorActive = false;
      if (dDeath > svStart) {
        survAmt = survivorCentsCalc(
          ePiaRaw,
          ePiaDol,
          eNra,
          eDri,
          eSsaBirth,
          eNraEpoch,
          eFile,
          eDeath,
          e70Epoch,
          dSurvNra,
          svStart,
          dSsaBirth
        );
        const depFinalPers = depZeroPia ? 0 : dPJ[fdI];
        if (depFinalPers < survAmt) isSurvivorActive = true;
      }

      // ── NPV sum for this filing pair ──
      let total = ePersNPV[feI];

      if (depZeroPia) {
        // Dep has no personal benefit; nothing to add.
      } else if (isSurvivorActive) {
        // isSurvivorActive implies dDeath > svStart (set at line above),
        // so dPersTruncNPV is the right choice here.
        total += dPersTruncNPV[fdI];
      } else {
        total += dPersFullNPV[fdI];
      }

      if (isSurvivorActive) {
        // Survivor period: svStart .. dDeath. Payment at end of month:
        // first payment at svStart+1, last at dDeath+1.
        total += npvConstant(survAmt, svStart + 1, dDeath + 1);
      }

      if (spEligible) {
        // Spousal period: max(eFile, dFile) .. min(svStart-1, dDeath).
        const spStart = eFile > dFile ? eFile : dFile;
        const spEnd = svStart - 1 < dDeath ? svStart - 1 : dDeath;
        if (spEnd >= spStart) {
          const spAmt = spousalCentsForPair(
            dPiaRaw,
            ePiaRaw,
            dFile,
            dNraEpoch,
            spStart,
            dfy,
            dpj
          );
          total += npvConstant(spAmt, spStart + 1, spEnd + 1);
        }
      }

      if (total > bestNPV) {
        bestNPV = total;
        bestF0 = f0;
        bestF1 = f1;
      }
    }
  }

  // Zero-PIA dep can't collect spousal until earner files; multiple
  // dependent filing ages below earner's produce identical NPV. The loop's
  // tie-break picks the earliest, which displays a misleading "62y1m". Bump
  // the reported dep age to match the earner's filing whenever needed.
  //
  // Not when the dependent is recorded in `alreadyFiled`: that month is a
  // fact the user entered, not a tie-break, and expectedNPVCoupleOptimized
  // reports it unchanged. Bumping it here would make the surfaces disagree.
  if (depZeroPia && alreadyFiled[dependentIndex] === null) {
    const bestFE = earnerIndex === 0 ? bestF0 : bestF1;
    const earnerFileEpoch = eSsaBirth + bestFE;
    let bestFD = earnerIndex === 0 ? bestF1 : bestF0;
    const depFileEpoch = dSsaBirth + bestFD;
    if (depFileEpoch < earnerFileEpoch) {
      // Cap at SSA age 70 — dep can't file past their max benefit age. This
      // matters when the earner is younger than the dep and files at 70:
      // their calendar filing month would map to a dep age above 70.
      // Clamp into the dependent's own searched range: reporting an age
      // below dStart would name a filing month they cannot file in.
      bestFD = Math.max(
        dStart,
        Math.min(earnerFileEpoch - dSsaBirth, MAX_BENEFIT_AGE_MONTHS)
      );
      if (earnerIndex === 0) bestF1 = bestFD;
      else bestF0 = bestFD;
    }
  }
  return [new MonthDuration(bestF0), new MonthDuration(bestF1), bestNPV];
}
